import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Phone, Mail, MapPin } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_a01f8d12-1bde-4fd2-8a5f-4dafd263008a/artifacts/d665bj3h_Global%20Wings%20fond%20noir.png";

const navLinks = [
  { name: 'Accueil', href: '/' },
  { name: 'Offres', href: '/offres' },
  { name: 'Services', href: '/#services' },
  { name: 'Visa', href: '/visa' },
  { name: 'Conciergerie', href: '/conciergerie' },
  { name: 'Contact', href: '/contact' }
];

export const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { user, isAdminOrAgent } = useAuth();

  return (
    <>
      {/* Top Bar */}
      <div className="bg-[#0B1121] border-b border-white/5 py-2.5 hidden lg:block">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex justify-between items-center">
          <div className="flex items-center gap-8">
            <a href="tel:+243975608422" className="flex items-center gap-2 text-[#94A3B8] hover:text-[#D4AF37] transition-colors text-sm">
              <Phone className="w-4 h-4" />
              <span>+243 975 608 422</span>
            </a>
            <a href="mailto:contact@globalwings-rdc.com" className="flex items-center gap-2 text-[#94A3B8] hover:text-[#D4AF37] transition-colors text-sm">
              <Mail className="w-4 h-4" />
              <span>contact@globalwings-rdc.com</span>
            </a>
          </div>
          <div className="flex items-center gap-2 text-[#64748B] text-sm">
            <MapPin className="w-4 h-4" />
            <span>Kinshasa, RDC</span>
            <span className="mx-2">|</span>
            <span>Dubaï, EAU</span>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <header className="sticky top-0 z-50 glass border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <Link to="/" className="flex-shrink-0">
              <motion.img 
                src={LOGO_URL} 
                alt="Global Wings Travel Consulting" 
                className="h-24 w-auto"
                whileHover={{ scale: 1.02 }}
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.href}
                  data-testid={`nav-${link.name.toLowerCase()}`}
                  className={`relative px-5 py-2.5 text-sm font-medium transition-colors ${
                    location.pathname === link.href
                      ? 'text-[#D4AF37]'
                      : 'text-[#94A3B8] hover:text-white'
                  }`}
                >
                  {link.name}
                  {location.pathname === link.href && (
                    <motion.div 
                      layoutId="navIndicator"
                      className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-[#D4AF37]"
                    />
                  )}
                </Link>
              ))}
            </nav>

            {/* CTA Buttons */}
            <div className="hidden lg:flex items-center gap-4">
              {user ? (
                <>
                  {isAdminOrAgent() && (
                    <Link to="/admin">
                      <button className="px-5 py-2.5 text-sm font-medium text-[#D4AF37] border border-[#D4AF37]/30 rounded-full hover:bg-[#D4AF37]/10 transition-colors">
                        Dashboard
                      </button>
                    </Link>
                  )}
                  <span className="text-[#64748B] text-sm">{user.full_name}</span>
                </>
              ) : (
                <Link to="/login">
                  <button className="px-5 py-2.5 text-sm font-medium text-[#94A3B8] hover:text-white transition-colors">
                    Connexion
                  </button>
                </Link>
              )}
              <a href="https://wa.me/243975608422" target="_blank" rel="noopener noreferrer" data-testid="header-whatsapp-btn">
                <button className="btn-gold px-6 py-2.5 text-sm">
                  Nous Contacter
                </button>
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2 text-white"
              onClick={() => setIsOpen(!isOpen)}
              data-testid="mobile-menu-btn"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden overflow-hidden bg-[#0B1121] border-t border-white/5"
            >
              <div className="px-6 py-6 space-y-2">
                {navLinks.map((link) => (
                  <Link
                    key={link.name}
                    to={link.href}
                    onClick={() => setIsOpen(false)}
                    className={`block px-4 py-3 rounded-xl text-base font-medium transition-colors ${
                      location.pathname === link.href
                        ? 'bg-[#D4AF37]/10 text-[#D4AF37]'
                        : 'text-[#94A3B8] hover:bg-white/5'
                    }`}
                  >
                    {link.name}
                  </Link>
                ))}
                <div className="pt-4 space-y-3">
                  {user ? (
                    isAdminOrAgent() && (
                      <Link to="/admin" onClick={() => setIsOpen(false)}>
                        <button className="w-full btn-gold py-3 text-sm">
                          Dashboard
                        </button>
                      </Link>
                    )
                  ) : (
                    <Link to="/login" onClick={() => setIsOpen(false)}>
                      <button className="w-full btn-glass py-3 text-sm text-white">
                        Connexion
                      </button>
                    </Link>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>
    </>
  );
};

export default Header;
